import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Smartphone, X, Download } from 'lucide-react';
import { referralApi } from '../api/client';

export default function ReferralTracker() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [refUserId, setRefUserId] = useState(null);

  useEffect(() => {
    // 1. Capture referral param if present in URL
    const params = new URLSearchParams(location.search);
    const refParam = params.get('ref');
    if (refParam) {
      localStorage.setItem('shopaxo_ref_user_id', refParam);
      setRefUserId(refParam);
    } else {
      const storedRef = localStorage.getItem('shopaxo_ref_user_id');
      if (storedRef) setRefUserId(storedRef);
    }

    // 2. Detect Android mobile browser (and not in native WebView bridge)
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isNativeApp = !!(window.AndroidBridge || window.ShopAxoBridge || window.Capacitor?.isNativePlatform());
      const isBannerDismissed = sessionStorage.getItem('shopaxo_app_banner_dismissed');

      if (isAndroid && !isNativeApp && !isBannerDismissed) {
        setShowBanner(true);
      }
    }
  }, [location]);

  const handleInstallAppClick = async () => {
    const storedRef = refUserId || localStorage.getItem('shopaxo_ref_user_id');
    
    // Log referral app download event if referrer ID exists
    if (storedRef) {
      try {
        await referralApi.trackAppDownload(storedRef);
      } catch (err) {
        console.error('Failed to log referral app download:', err);
      }
    }

    // Play Store / Android Intent fallback
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.shopaxo.app';
    const appIntentUrl = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.shopaxo.app;end;`;

    try {
      // Try launching app deep link first
      window.location.href = appIntentUrl;
      setTimeout(() => {
        window.location.href = playStoreUrl;
      }, 1500);
    } catch {
      window.location.href = playStoreUrl;
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('shopaxo_app_banner_dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'calc(100% - 32px)',
      maxWidth: '480px',
      background: 'linear-gradient(135deg, #4F46E5, #6366F1)',
      color: '#ffffff',
      padding: '12px 16px',
      borderRadius: '16px',
      boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justify: 'space-between',
      gap: '12px',
      animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Smartphone size={20} color="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Get the ShopAXO Android App!
          </div>
          <div style={{ fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>
            Faster shopping & instant alerts
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleInstallAppClick}
          style={{
            background: '#ffffff',
            color: '#4F46E5',
            border: 'none',
            padding: '8px 14px',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
          }}
        >
          <Download size={13} />
          Open / Install
        </button>

        <button
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
